import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
  ShoppingBag,
  DollarSign,
  Users,
  Activity,
  AlertTriangle,
  MessageSquare,
  Package,
  Layers,
  Archive,
  RefreshCw,
  ExternalLink,
  ChevronRight,
  ArrowUpRight,
  Tag,
  CreditCard,
  Truck,
  CheckCircle2,
  Clock,
  Calendar,
} from "lucide-react";
import { fetchAdminDashboardStats } from "../../api/adminApi";
import { formatMoney, formatDate, formatOrderStatus } from "../../utils/format";
import "./AdminDashboard.css";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeframe, setTimeframe] = useState(30); // 7, 14, 30 days
  const [chartMetric, setChartMetric] = useState("revenue"); // 'revenue' | 'orders'
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminDashboardStats(timeframe);
      setStats(data);
    } catch (err) {
      console.error("Failed to load dashboard statistics:", err);
      setError(err?.message || "Failed to load dashboard statistics.");
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // SVG Chart Geometry Calculations
  const chartConfig = useMemo(() => {
    if (!stats || !stats.dailySales || stats.dailySales.length === 0) {
      return null;
    }

    const points = stats.dailySales;
    const width = 800;
    const height = 260;
    const padding = { top: 25, right: 30, bottom: 45, left: 65 };
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;

    const values = points.map((p) =>
      chartMetric === "revenue" ? Number(p.revenue || 0) : Number(p.ordersCount || 0)
    );
    const maxVal = Math.max(...values, chartMetric === "revenue" ? 500 : 5);
    const minVal = 0;
    const range = maxVal - minVal || 1;

    // Grid ticks (4 horizontal lines)
    const ticks = [0, 0.25, 0.5, 0.75, 1].map((pct) => ({
      val: minVal + range * pct,
      y: padding.top + innerH - innerH * pct,
    }));

    // Plot coordinates
    const coords = points.map((p, idx) => {
      const val = chartMetric === "revenue" ? Number(p.revenue || 0) : Number(p.ordersCount || 0);
      const x = padding.left + (idx / (points.length - 1 || 1)) * innerW;
      const y = padding.top + innerH - ((val - minVal) / range) * innerH;
      return { x, y, val, date: p.date, label: p.label, raw: p };
    });

    // Create SVG path
    let linePath = "";
    let areaPath = "";

    if (coords.length > 0) {
      linePath = `M ${coords[0].x} ${coords[0].y}`;
      for (let i = 1; i < coords.length; i++) {
        // Smooth cubic bezier or straight lines
        const prev = coords[i - 1];
        const curr = coords[i];
        const cpX = (prev.x + curr.x) / 2;
        linePath += ` C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
      }

      const lastX = coords[coords.length - 1].x;
      const firstX = coords[0].x;
      const bottomY = padding.top + innerH;
      areaPath = `${linePath} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;
    }

    return {
      width,
      height,
      padding,
      innerW,
      innerH,
      ticks,
      coords,
      linePath,
      areaPath,
      maxVal,
    };
  }, [stats, chartMetric]);

  // Donut chart status segments
  const donutSegments = useMemo(() => {
    if (!stats || !stats.ordersByStatus || stats.ordersByStatus.length === 0) {
      return [];
    }

    const colors = {
      Confirmed: "#10b981", // Emerald
      Shipping: "#3b82f6", // Blue
      Completed: "#c9a96e", // Gold
      Pending: "#f59e0b", // Amber
      RequiresReview: "#eab308", // Yellow
      Cancelled: "#ef4444", // Red
      Returned: "#64748b", // Slate
    };

    let cumulativePct = 0;
    return stats.ordersByStatus.map((item) => {
      const pct = item.percentage / 100;
      const startAngle = cumulativePct * 360;
      cumulativePct += pct;
      const endAngle = cumulativePct * 360;
      return {
        ...item,
        color: colors[item.statusName] || "#8a7a6a",
        startAngle,
        endAngle,
      };
    });
  }, [stats]);

  return (
    <div className="admin-dashboard">
      {/* ── Header ── */}
      <div className="admin-dash-header">
        <div className="admin-dash-header__left">
          <span className="section-label">Executive Overview</span>
          <h1 className="admin-dash-title">Store Intelligence</h1>
          <p className="admin-dash-sub">
            Real-time financial telemetry, order fulfillment pulse, and catalogue health.
          </p>
        </div>

        <div className="admin-dash-header__right">
          {/* Timeframe Selector */}
          <div className="admin-timeframe-pills">
            {[7, 14, 30].map((days) => (
              <button
                key={days}
                type="button"
                className={`admin-timeframe-btn ${timeframe === days ? "active" : ""}`}
                onClick={() => setTimeframe(days)}
              >
                {days}D
              </button>
            ))}
          </div>

          <button
            type="button"
            className="btn-outline admin-refresh-btn"
            onClick={loadData}
            disabled={loading}
            title="Refresh statistics"
          >
            <RefreshCw size={14} className={loading ? "spin-animate" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* ── Operational Pulse Alert Bar ── */}
      {stats && (
        <div className="admin-pulse-bar">
          {stats.pendingOrders > 0 && (
            <Link to="/admin/orders?status=1" className="admin-pulse-chip admin-pulse-chip--warning">
              <Clock size={14} />
              <span>
                <strong>{stats.pendingOrders}</strong> Orders Require Fulfillment
              </span>
              <ChevronRight size={13} />
            </Link>
          )}

          {stats.unreadInquiriesCount > 0 && (
            <Link to="/admin/inquiries?filter=unread" className="admin-pulse-chip admin-pulse-chip--info">
              <MessageSquare size={14} />
              <span>
                <strong>{stats.unreadInquiriesCount}</strong> Unread Customer Inquiries
              </span>
              <ChevronRight size={13} />
            </Link>
          )}

          {stats.lowStockItemsCount > 0 && (
            <Link to="/admin/stock" className="admin-pulse-chip admin-pulse-chip--danger">
              <AlertTriangle size={14} />
              <span>
                <strong>{stats.lowStockItemsCount}</strong> Variants in Low/Zero Stock
              </span>
              <ChevronRight size={13} />
            </Link>
          )}
        </div>
      )}

      {/* ── Error Banner ── */}
      {error && (
        <div className="admin-dash-error">
          <AlertTriangle size={18} />
          <span>{error}</span>
          <button type="button" onClick={loadData}>
            Retry
          </button>
        </div>
      )}

      {/* ── 4 Main KPI Cards ── */}
      <div className="admin-kpi-grid">
        {/* KPI 1: Total Revenue */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-card__top">
            <span className="admin-kpi-card__label">Total Revenue</span>
            <div className="admin-kpi-card__icon-box admin-kpi-card__icon-box--gold">
              <DollarSign size={18} />
            </div>
          </div>
          <div className="admin-kpi-card__value">
            {loading ? "…" : formatMoney(stats?.totalRevenue || 0, "en")}
          </div>
          <div className="admin-kpi-card__footer">
            {stats && stats.revenueGrowthPercentage !== undefined && (
              <span
                className={`admin-trend-badge ${
                  stats.revenueGrowthPercentage >= 0
                    ? "admin-trend-badge--up"
                    : "admin-trend-badge--down"
                }`}
              >
                {stats.revenueGrowthPercentage >= 0 ? (
                  <TrendingUp size={13} />
                ) : (
                  <TrendingDown size={13} />
                )}
                <span>
                  {stats.revenueGrowthPercentage >= 0 ? "+" : ""}
                  {stats.revenueGrowthPercentage}%
                </span>
              </span>
            )}
            <span className="admin-kpi-card__subtext">
              {formatMoney(stats?.monthlyRevenue || 0, "en")} this month
            </span>
          </div>
        </div>

        {/* KPI 2: Total Orders */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-card__top">
            <span className="admin-kpi-card__label">Total Orders</span>
            <div className="admin-kpi-card__icon-box admin-kpi-card__icon-box--emerald">
              <ShoppingBag size={18} />
            </div>
          </div>
          <div className="admin-kpi-card__value">
            {loading ? "…" : stats?.totalOrders || 0}
          </div>
          <div className="admin-kpi-card__footer">
            <span className="admin-kpi-card__subtext">
              <strong style={{ color: "var(--text-primary)" }}>
                {stats?.completedOrders || 0}
              </strong>{" "}
              completed ·{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {stats?.shippedOrders || 0}
              </strong>{" "}
              in transit
            </span>
          </div>
        </div>

        {/* KPI 3: Average Order Value */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-card__top">
            <span className="admin-kpi-card__label">Average Order Value</span>
            <div className="admin-kpi-card__icon-box admin-kpi-card__icon-box--blue">
              <Activity size={18} />
            </div>
          </div>
          <div className="admin-kpi-card__value">
            {loading ? "…" : formatMoney(stats?.averageOrderValue || 0, "en")}
          </div>
          <div className="admin-kpi-card__footer">
            <span className="admin-kpi-card__subtext">
              Calculated across all confirmed purchases
            </span>
          </div>
        </div>

        {/* KPI 4: Customers */}
        <div className="admin-kpi-card">
          <div className="admin-kpi-card__top">
            <span className="admin-kpi-card__label">Registered Accounts</span>
            <div className="admin-kpi-card__icon-box admin-kpi-card__icon-box--purple">
              <Users size={18} />
            </div>
          </div>
          <div className="admin-kpi-card__value">
            {loading ? "…" : stats?.totalCustomers || 0}
          </div>
          <div className="admin-kpi-card__footer">
            <span className="admin-kpi-card__subtext">
              Excluding guest checkout profiles
            </span>
          </div>
        </div>
      </div>

      {/* ── Main Analytics Section: Chart + Order Status Donut ── */}
      <div className="admin-charts-grid">
        {/* Sales & Revenue Trend Chart */}
        <div className="admin-chart-card admin-chart-card--main">
          <div className="admin-chart-card__header">
            <div>
              <h2 className="admin-chart-title">Sales Trajectory</h2>
              <p className="admin-chart-sub">
                Daily {chartMetric === "revenue" ? "revenue (EGP)" : "volume of orders"} over the
                last {timeframe} days.
              </p>
            </div>

            <div className="admin-chart-toggle-group">
              <button
                type="button"
                className={`admin-toggle-pill ${chartMetric === "revenue" ? "active" : ""}`}
                onClick={() => setChartMetric("revenue")}
              >
                Revenue
              </button>
              <button
                type="button"
                className={`admin-toggle-pill ${chartMetric === "orders" ? "active" : ""}`}
                onClick={() => setChartMetric("orders")}
              >
                Orders
              </button>
            </div>
          </div>

          <div className="admin-svg-chart-container">
            {chartConfig ? (
              <svg
                viewBox={`0 0 ${chartConfig.width} ${chartConfig.height}`}
                className="admin-svg-chart"
                onMouseLeave={() => setHoveredPoint(null)}
              >
                <defs>
                  <linearGradient id="goldAreaGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c9a96e" stopOpacity="0.32" />
                    <stop offset="100%" stopColor="#c9a96e" stopOpacity="0.0" />
                  </linearGradient>
                </defs>

                {/* Horizontal Gridlines */}
                {chartConfig.ticks.map((tick, idx) => (
                  <g key={idx}>
                    <line
                      x1={chartConfig.padding.left}
                      y1={tick.y}
                      x2={chartConfig.width - chartConfig.padding.right}
                      y2={tick.y}
                      stroke="var(--border)"
                      strokeDasharray="4 4"
                      strokeWidth="1"
                    />
                    <text
                      x={chartConfig.padding.left - 12}
                      y={tick.y + 4}
                      className="admin-chart-axis-label"
                      textAnchor="end"
                    >
                      {chartMetric === "revenue"
                        ? tick.val >= 1000
                          ? `${(tick.val / 1000).toFixed(0)}k`
                          : tick.val.toFixed(0)
                        : tick.val.toFixed(0)}
                    </text>
                  </g>
                ))}

                {/* Filled Area */}
                {chartConfig.areaPath && (
                  <path d={chartConfig.areaPath} fill="url(#goldAreaGradient)" />
                )}

                {/* Curved Stroke Line */}
                {chartConfig.linePath && (
                  <path
                    d={chartConfig.linePath}
                    fill="none"
                    stroke="#c9a96e"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}

                {/* Interactive Points & Vertical Crosshairs */}
                {chartConfig.coords.map((pt, idx) => {
                  const isHovered = hoveredPoint && hoveredPoint.date === pt.date;

                  // Render X-axis date labels for subset of points (e.g. every 3rd or 5th)
                  const showLabel =
                    timeframe === 7
                      ? true
                      : timeframe === 14
                      ? idx % 2 === 0
                      : idx % 5 === 0 || idx === chartConfig.coords.length - 1;

                  return (
                    <g key={idx}>
                      {showLabel && (
                        <text
                          x={pt.x}
                          y={chartConfig.height - 12}
                          className="admin-chart-axis-label admin-chart-axis-label--x"
                          textAnchor="middle"
                        >
                          {pt.label}
                        </text>
                      )}

                      {/* Transparent Hover Target Area */}
                      <rect
                        x={pt.x - chartConfig.innerW / chartConfig.coords.length / 2}
                        y={chartConfig.padding.top}
                        width={chartConfig.innerW / chartConfig.coords.length}
                        height={chartConfig.innerH}
                        fill="transparent"
                        onMouseEnter={() => setHoveredPoint(pt)}
                        style={{ cursor: "pointer" }}
                      />

                      {/* Active / Hovered Highlight */}
                      {isHovered && (
                        <>
                          <line
                            x1={pt.x}
                            y1={chartConfig.padding.top}
                            x2={pt.x}
                            y2={chartConfig.padding.top + chartConfig.innerH}
                            stroke="#c9a96e"
                            strokeWidth="1.5"
                            strokeDasharray="3 3"
                          />
                          <circle
                            cx={pt.x}
                            cy={pt.y}
                            r="5"
                            fill="#14110f"
                            stroke="#c9a96e"
                            strokeWidth="2.5"
                          />
                        </>
                      )}
                    </g>
                  );
                })}
              </svg>
            ) : (
              <div className="admin-chart-empty">
                {loading ? "Synthesizing time-series telemetry…" : "No order data found."}
              </div>
            )}

            {/* Hover Floating Tooltip */}
            {hoveredPoint && (
              <div
                className="admin-chart-tooltip"
                style={{
                  left: `${(hoveredPoint.x / (chartConfig?.width || 1)) * 100}%`,
                }}
              >
                <div className="admin-tooltip-date">{hoveredPoint.label}</div>
                <div className="admin-tooltip-val">
                  <strong>{formatMoney(hoveredPoint.raw.revenue, "en")}</strong>
                </div>
                <div className="admin-tooltip-sub">
                  {hoveredPoint.raw.ordersCount} orders placed
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Status Distribution Card */}
        <div className="admin-chart-card">
          <div className="admin-chart-card__header">
            <div>
              <h2 className="admin-chart-title">Status Breakdown</h2>
              <p className="admin-chart-sub">Fulfillment distribution across all orders.</p>
            </div>
          </div>

          <div className="admin-donut-wrapper">
            {/* SVG Donut */}
            <div className="admin-donut-graphic">
              <svg viewBox="0 0 160 160" className="admin-donut-svg">
                <circle
                  cx="80"
                  cy="80"
                  r="62"
                  fill="none"
                  stroke="var(--border)"
                  strokeWidth="16"
                />
                {donutSegments.map((seg, idx) => {
                  const circumference = 2 * Math.PI * 62;
                  const strokeDasharray = `${(seg.percentage / 100) * circumference} ${circumference}`;
                  const strokeDashoffset = -((seg.startAngle / 360) * circumference);

                  return (
                    <circle
                      key={idx}
                      cx="80"
                      cy="80"
                      r="62"
                      fill="none"
                      stroke={seg.color}
                      strokeWidth="16"
                      strokeDasharray={strokeDasharray}
                      strokeDashoffset={strokeDashoffset}
                      transform="rotate(-90 80 80)"
                      className="admin-donut-segment"
                    />
                  );
                })}
              </svg>
              <div className="admin-donut-center">
                <span className="admin-donut-total">{stats?.totalOrders || 0}</span>
                <span className="admin-donut-label">Orders</span>
              </div>
            </div>

            {/* Status Legend & Progress Bars */}
            <div className="admin-status-legend">
              {stats?.ordersByStatus?.map((item, idx) => {
                const colorMap = {
                  Confirmed: "#10b981",
                  Shipping: "#3b82f6",
                  Completed: "#c9a96e",
                  Pending: "#f59e0b",
                  RequiresReview: "#eab308",
                  Cancelled: "#ef4444",
                  Returned: "#64748b",
                };
                const color = colorMap[item.statusName] || "var(--gold)";

                return (
                  <div key={idx} className="admin-status-row">
                    <div className="admin-status-row__header">
                      <span className="admin-status-pill">
                        <span
                          className="admin-status-dot"
                          style={{ backgroundColor: color }}
                        />
                        <span>{item.statusName}</span>
                      </span>
                      <span className="admin-status-counts">
                        <strong>{item.count}</strong> ({item.percentage}%)
                      </span>
                    </div>
                    <div className="admin-status-progress-track">
                      <div
                        className="admin-status-progress-bar"
                        style={{
                          width: `${item.percentage}%`,
                          backgroundColor: color,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Two-Column Operational Grid: Top Products & Low Stock ── */}
      <div className="admin-ops-grid">
        {/* Top Selling Products */}
        <div className="admin-table-card">
          <div className="admin-table-card__header">
            <div>
              <h2 className="admin-chart-title">Best Sellers</h2>
              <p className="admin-chart-sub">Top revenue-generating catalog items.</p>
            </div>
            <Link to="/admin/products" className="admin-table-card__link">
              <span>View Catalogue</span>
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="admin-top-products-list">
            {stats?.topSellingProducts && stats.topSellingProducts.length > 0 ? (
              stats.topSellingProducts.map((p, idx) => (
                <div key={p.productId} className="admin-top-product-item">
                  <div className="admin-top-product-rank">#{idx + 1}</div>
                  <div className="admin-top-product-info">
                    <h4 className="admin-top-product-name">{p.name}</h4>
                    {p.arabicName && (
                      <span className="admin-top-product-ar">{p.arabicName}</span>
                    )}
                  </div>
                  <div className="admin-top-product-metrics">
                    <span className="admin-top-product-units">
                      {p.unitsSold} units sold
                    </span>
                    <span className="admin-top-product-revenue">
                      {formatMoney(p.revenue, "en")}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-empty-table">
                {loading ? "Loading best sellers…" : "No sales recorded yet."}
              </div>
            )}
          </div>
        </div>

        {/* Low Stock Watchlist */}
        <div className="admin-table-card">
          <div className="admin-table-card__header">
            <div>
              <h2 className="admin-chart-title">Low Stock Watchlist</h2>
              <p className="admin-chart-sub">
                Variants with critical inventory (5 units or fewer).
              </p>
            </div>
            <Link to="/admin/stock" className="admin-table-card__link">
              <span>Manage Stock</span>
              <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="admin-low-stock-list">
            {stats?.lowStockItems && stats.lowStockItems.length > 0 ? (
              stats.lowStockItems.map((item, idx) => (
                <div key={idx} className="admin-low-stock-item">
                  <div className="admin-low-stock-info">
                    <h4 className="admin-low-stock-title">{item.ProductName}</h4>
                    <div className="admin-low-stock-tags">
                      {item.ColorName && (
                        <span className="admin-variant-pill">
                          {item.ColorHex && (
                            <span
                              className="admin-color-swatch"
                              style={{ backgroundColor: item.ColorHex }}
                            />
                          )}
                          <span>{item.ColorName}</span>
                        </span>
                      )}
                      {item.SizeName && (
                        <span className="admin-variant-pill">
                          Size: {item.SizeName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="admin-low-stock-action">
                    <span
                      className={`admin-stock-qty-badge ${
                        item.Quantity === 0 ? "admin-stock-qty-badge--out" : ""
                      }`}
                    >
                      {item.Quantity === 0 ? "Out of Stock" : `${item.Quantity} left`}
                    </span>
                    <Link
                      to={`/admin/stock`}
                      className="admin-restock-btn"
                      title="Adjust Inventory"
                    >
                      Restock
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-empty-table admin-empty-table--good">
                <CheckCircle2 size={24} style={{ color: "#10b981", margin: "0 auto 8px" }} />
                <p>All product variants are healthy with adequate inventory.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Recent Orders Feed ── */}
      <div className="admin-table-card" style={{ marginTop: "24px" }}>
        <div className="admin-table-card__header">
          <div>
            <h2 className="admin-chart-title">Recent Transactions</h2>
            <p className="admin-chart-sub">Latest customer purchases across all channels.</p>
          </div>
          <Link to="/admin/orders" className="admin-table-card__link">
            <span>View All Orders</span>
            <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="admin-orders-table-wrapper">
          <table className="admin-orders-table">
            <thead>
              <tr>
                <th>Order Ref</th>
                <th>Customer</th>
                <th>Date</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Total</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {stats?.recentOrders && stats.recentOrders.length > 0 ? (
                stats.recentOrders.map((order) => {
                  const statusLabel = formatOrderStatus(order.orderStatus, "en");
                  return (
                    <tr key={order.orderId}>
                      <td>
                        <Link
                          to={`/admin/orders/${order.orderId}`}
                          className="admin-order-id-link"
                        >
                          #{order.orderId.substring(0, 8)}…
                        </Link>
                      </td>
                      <td>
                        <div className="admin-order-cust">
                          <span className="admin-order-cust__name">
                            {order.customerName}
                          </span>
                          {order.customerEmail && (
                            <span className="admin-order-cust__email">
                              {order.customerEmail}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="admin-order-date">
                          {formatDate(order.createdAt, "en")}
                        </span>
                      </td>
                      <td>
                        <span className="admin-pay-pill">
                          <CreditCard size={12} />
                          <span>{order.paymentMethod === 1 ? "Card" : "COD"}</span>
                        </span>
                      </td>
                      <td>
                        <span className={`admin-badge-status admin-badge-status--${statusLabel.toLowerCase()}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td>
                        <strong className="admin-order-price">
                          {formatMoney(order.totalPrice, "en")}
                        </strong>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Link
                          to={`/admin/orders/${order.orderId}`}
                          className="btn-outline admin-view-order-btn"
                        >
                          Details
                        </Link>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center", padding: "32px" }}>
                    {loading ? "Loading transactions…" : "No orders found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Quick Jump Navigation Tiles ── */}
      <div className="admin-quick-tiles-grid" style={{ marginTop: "32px" }}>
        <Link to="/admin/orders" className="admin-quick-tile">
          <Package size={20} className="admin-quick-tile__icon" />
          <div className="admin-quick-tile__text">
            <h3>Orders Management</h3>
            <p>Fulfill orders, track Bosta shipments, handle COD collections.</p>
          </div>
          <ChevronRight size={16} className="admin-quick-tile__arrow" />
        </Link>

        <Link to="/admin/products" className="admin-quick-tile">
          <ShoppingBag size={20} className="admin-quick-tile__icon" />
          <div className="admin-quick-tile__text">
            <h3>Product Catalogue</h3>
            <p>Create and edit products, upload imagery, and configure variants.</p>
          </div>
          <ChevronRight size={16} className="admin-quick-tile__arrow" />
        </Link>

        <Link to="/admin/collections" className="admin-quick-tile">
          <Layers size={20} className="admin-quick-tile__icon" />
          <div className="admin-quick-tile__text">
            <h3>Collections & Banners</h3>
            <p>Organize categories and upload editorial banners.</p>
          </div>
          <ChevronRight size={16} className="admin-quick-tile__arrow" />
        </Link>

        <Link to="/admin/stock" className="admin-quick-tile">
          <Archive size={20} className="admin-quick-tile__icon" />
          <div className="admin-quick-tile__text">
            <h3>Inventory & Stock</h3>
            <p>Adjust variant stock quantities and view inventory counts.</p>
          </div>
          <ChevronRight size={16} className="admin-quick-tile__arrow" />
        </Link>

        <Link to="/admin/inquiries" className="admin-quick-tile">
          <MessageSquare size={20} className="admin-quick-tile__icon" />
          <div className="admin-quick-tile__text">
            <h3>Customer Inquiries</h3>
            <p>Review contact messages, save internal notes, and reply.</p>
          </div>
          <ChevronRight size={16} className="admin-quick-tile__arrow" />
        </Link>
      </div>
    </div>
  );
}
